import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSymbols, type SymbolSearchResult } from '../api/symbols';
import { useKeyboardSafeModal } from '../hooks/useKeyboardSafeModal';
import { getProviderDisplayName } from '../lib/providerRouting';

export interface SelectedSymbolOption {
  symbol: string;
  provider: SymbolSearchResult['provider'];
}

type SourceFilter = 'All' | SymbolSearchResult['provider'];

interface SymbolSearchModalProps {
  open: boolean;
  currentSymbol: string;
  currentProvider: SymbolSearchResult['provider'];
  onClose: () => void;
  onSelectSymbol: (selection: SelectedSymbolOption) => void;
}

type SymbolCategory =
  | 'All'
  | 'Stocks'
  | 'Funds'
  | 'Futures'
  | 'Forex'
  | 'Crypto'
  | 'Indices'
  | 'Bonds'
  | 'Economy'
  | 'Options';

const SYMBOL_CATEGORIES: SymbolCategory[] = ['All', 'Stocks', 'Funds', 'Futures', 'Forex', 'Crypto', 'Indices', 'Bonds', 'Economy', 'Options'];
const BROWSE_PAGE_SIZE = 100;

function buildMetadataLine(result: SymbolSearchResult): string {
  const values = [result.type, result.category, result.exchangeDisplay ?? result.exchange]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map((value) => value.trim());
  if (values.length === 0) {
    return 'No metadata available';
  }
  return values.join(' · ');
}

export function SymbolSearchModal({
  open,
  currentSymbol,
  currentProvider,
  onClose,
  onSelectSymbol,
}: SymbolSearchModalProps) {
  const [query, setQuery] = useState('');
  const [symbols, setSymbols] = useState<SymbolSearchResult[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SymbolCategory>('All');
  const [selectedSource, setSelectedSource] = useState<SourceFilter>('All');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [catalogWarming, setCatalogWarming] = useState(false);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchVersionRef = useRef(0);
  const { dialogAutoFocus, dialogRef, isKeyboardSafeLayout, viewportStyle } = useKeyboardSafeModal(open);

  const trimmedQuery = query.trim();
  const isBrowseMode = trimmedQuery.length === 0;
  const availableSources = Array.from(new Set(symbols.map((result) => result.provider))).sort((left, right) => {
    return getProviderDisplayName(left).localeCompare(getProviderDisplayName(right));
  });
  const visibleSymbols = selectedSource === 'All' ? symbols : symbols.filter((result) => result.provider === selectedSource);

  const loadMoreBrowseResults = useCallback(async () => {
    if (!open || !isBrowseMode || nextOffset === null || isLoading || isLoadingMore || errorMessage) {
      return;
    }
    const searchVersion = searchVersionRef.current;
    setIsLoadingMore(true);
    try {
      const response = await fetchSymbols('', selectedCategory, {
        offset: nextOffset,
        limit: BROWSE_PAGE_SIZE,
      });
      if (searchVersionRef.current !== searchVersion) {
        return;
      }
      setSymbols((currentSymbols) => currentSymbols.concat(response.symbols));
      setCatalogWarming(response.catalogWarming);
      setNextOffset(response.nextOffset);
    } catch (error: unknown) {
      if (searchVersionRef.current !== searchVersion) {
        return;
      }
      const message = error instanceof Error ? error.message : 'Failed to load more symbols.';
      setErrorMessage(message);
    } finally {
      if (searchVersionRef.current === searchVersion) {
        setIsLoadingMore(false);
      }
    }
  }, [errorMessage, isBrowseMode, isLoading, isLoadingMore, nextOffset, open, selectedCategory]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSymbols([]);
      setSelectedCategory('All');
      setSelectedSource('All');
      setErrorMessage(null);
      setIsLoading(false);
      setIsLoadingMore(false);
      setCatalogWarming(false);
      setNextOffset(null);
      return;
    }

    let isActive = true;
    const searchVersion = searchVersionRef.current + 1;
    searchVersionRef.current = searchVersion;
    setIsLoading(true);
    setIsLoadingMore(false);
    setErrorMessage(null);

    void fetchSymbols(trimmedQuery, selectedCategory, isBrowseMode ? { limit: BROWSE_PAGE_SIZE } : {})
      .then((response) => {
        if (!isActive || searchVersionRef.current !== searchVersion) {
          return;
        }
        setSymbols(response.symbols);
        setCatalogWarming(response.catalogWarming);
        setNextOffset(response.nextOffset);
      })
      .catch((error: unknown) => {
        if (!isActive || searchVersionRef.current !== searchVersion) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to load symbols.';
        setErrorMessage(message);
        setNextOffset(null);
      })
      .finally(() => {
        if (isActive && searchVersionRef.current === searchVersion) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [isBrowseMode, open, selectedCategory, trimmedQuery]);

  useEffect(() => {
    if (selectedSource === 'All') {
      return;
    }
    if (!availableSources.includes(selectedSource)) {
      setSelectedSource('All');
    }
  }, [availableSources, selectedSource]);

  useEffect(() => {
    if (!open || !isBrowseMode || nextOffset === null || isLoading || isLoadingMore || errorMessage) {
      return;
    }
    const container = listRef.current;
    if (container === null) {
      return;
    }

    const onScroll = () => {
      const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (remaining <= 180) {
        void loadMoreBrowseResults();
      }
    };

    container.addEventListener('scroll', onScroll);
    onScroll();
    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, [errorMessage, isBrowseMode, isLoading, isLoadingMore, loadMoreBrowseResults, nextOffset, open]);

  if (!open) {
    return null;
  }

  return (
    <div
      data-testid="symbol-search-backdrop"
      className={`fixed inset-0 z-50 flex bg-slate-950/70 backdrop-blur-sm ${
        isKeyboardSafeLayout ? 'items-start justify-stretch p-3 sm:p-4' : 'items-center justify-center p-4'
      }`}
      style={viewportStyle}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Symbol Search"
        tabIndex={-1}
        className={`w-full border border-border-subtle bg-surface-elevated shadow-[0_26px_60px_rgba(2,8,24,0.68)] ring-1 ring-slate-100/5 ${
          isKeyboardSafeLayout
            ? 'flex max-h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl'
            : 'max-w-2xl rounded-2xl'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-100">Symbol Search</h2>
            <p className="text-xs text-slate-400">Search instruments and switch chart symbol.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 transition-colors hover:bg-surface-panel/70 hover:text-slate-100"
            aria-label="Close symbol search"
          >
            X
          </button>
        </div>

        <div className="border-b border-border-subtle px-4 py-3">
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search symbol"
            autoFocus={dialogAutoFocus}
            className="w-full rounded-md border border-border-subtle bg-surface-base/85 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-400 hover:border-brand-primary/45 focus:border-brand-primary/65 focus:outline-hidden"
          />
        </div>

        <div ref={listRef} className={`space-y-2 overflow-y-auto p-4 ${isKeyboardSafeLayout ? 'min-h-0 flex-1' : 'max-h-[55vh]'}`}>
          <div className="flex flex-wrap gap-2">
            {SYMBOL_CATEGORIES.map((category) => {
              const isActive = category === selectedCategory;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    isActive
                      ? 'border-brand-primary/70 bg-brand-primary/20 text-slate-100'
                      : 'border-border-subtle bg-surface-base/60 text-slate-300 hover:border-brand-primary/45 hover:text-slate-100'
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border-subtle bg-surface-base/40 px-3 py-2">
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">Sources</span>
            <button
              type="button"
              onClick={() => setSelectedSource('All')}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                selectedSource === 'All'
                  ? 'border-brand-primary/70 bg-brand-primary/20 text-slate-100'
                  : 'border-border-subtle bg-surface-base/60 text-slate-300 hover:border-brand-primary/45 hover:text-slate-100'
              }`}
            >
              All sources
            </button>
            {availableSources.map((source) => {
              const isActive = selectedSource === source;
              return (
                <button
                  key={source}
                  type="button"
                  onClick={() => setSelectedSource(source)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    isActive
                      ? 'border-brand-primary/70 bg-brand-primary/20 text-slate-100'
                      : 'border-border-subtle bg-surface-base/60 text-slate-300 hover:border-brand-primary/45 hover:text-slate-100'
                  }`}
                >
                  {getProviderDisplayName(source)}
                </button>
              );
            })}
          </div>

          {isLoading ? (
            <p className="rounded-lg border border-border-subtle bg-surface-base/50 px-3 py-4 text-sm text-slate-300">
              Loading symbols...
            </p>
          ) : null}

          {errorMessage ? (
            <p className="rounded-lg border border-rose-400/45 bg-rose-500/10 px-3 py-4 text-sm text-rose-100">
              {errorMessage}
            </p>
          ) : null}

          {!isLoading && !errorMessage && isLoadingMore ? (
            <p className="rounded-lg border border-border-subtle bg-surface-base/50 px-3 py-2 text-xs text-slate-400">
              Loading more symbols...
            </p>
          ) : null}

          {!isLoading && !errorMessage && catalogWarming ? (
            <p className="rounded-lg border border-amber-400/35 bg-amber-500/10 px-3 py-3 text-xs text-amber-100">
              Search catalog is still warming. Results only include symbols already cached on the server.
            </p>
          ) : null}

          {!isLoading && !errorMessage
            ? visibleSymbols.map((result) => {
                const isCurrent = result.symbol === currentSymbol && result.provider === currentProvider;
                return (
                  <button
                    key={`${result.provider}-${result.symbol}-${result.exchange ?? 'na'}-${result.type ?? 'na'}`}
                    type="button"
                    onClick={() => onSelectSymbol({ symbol: result.symbol, provider: result.provider })}
                    className={`grid w-full grid-cols-[minmax(0,120px)_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                      isCurrent
                        ? 'border-brand-primary/60 bg-brand-primary/15 text-slate-100'
                        : 'border-border-subtle bg-surface-base/70 text-slate-100 hover:border-brand-primary/45 hover:bg-surface-panel/80'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-slate-100">{result.symbol}</span>
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-slate-100">{result.fullName}</span>
                      <span className="block truncate text-xs text-slate-400">{result.description}</span>
                    </span>
                    <span className="flex min-w-0 items-center gap-2 justify-self-end">
                      <span className="hidden max-w-[220px] truncate text-[11px] text-slate-400 sm:block">{buildMetadataLine(result)}</span>
                      <span className="rounded-full border border-sky-400/35 bg-sky-500/10 px-2 py-0.5 text-[11px] font-medium text-sky-100">
                        {getProviderDisplayName(result.provider)}
                      </span>
                    {isCurrent ? (
                      <span className="rounded-full border border-brand-accent/70 bg-brand-primary/20 px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.13em] text-brand-accent">
                        Current
                      </span>
                    ) : null}
                    </span>
                  </button>
                );
              })
            : null}

          {!isLoading && !errorMessage && visibleSymbols.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border-subtle px-3 py-4 text-center text-sm text-slate-400">
              {symbols.length > 0 && selectedSource !== 'All'
                ? `No ${getProviderDisplayName(selectedSource)} symbols match the current view.`
                : query.trim().length === 0
                  ? selectedCategory === 'All'
                  ? 'No popular symbols are cached yet.'
                  : `No ${selectedCategory.toLowerCase()} symbols are cached yet.`
                  : 'No symbols found.'}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
